import { useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useNavigate, useLocation } from "react-router-dom";
import { empty, errorResponse, isArray, prepareResponseData } from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";

// css
import "../students/Students.css";

// api
import parentApi from "../../api/Parents";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import TableLoading from "../../components/skeleton/TableLoading";
import { FaPlus } from "react-icons/fa";
import ParentsTable from "../../components/tables/primeTable/parents/ParentsTable";

const Parents = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  //ref
  const toastTR = useRef(null);
  // states
  const [parentsData, setParentsData] = useState([]);
  const [parentsModalVisibility, setParentsModalVisibility] = useState(false);
  const [selectedParentDetail, setSelectedParentsDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [modalType, setModalType] = useState("update");
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    if (!empty(location) && !empty(location.state)) {
      const paramState = !empty(location.state) ? location.state : {};
      let parentAdded = !empty(paramState.parentAdded)
        ? paramState.parentAdded
        : false;
      let parentUpdated = !empty(paramState.parentUpdated)
        ? paramState.parentUpdated
        : false;
      if (parentAdded === true || parentUpdated === true) {
        const actionType = parentAdded
          ? "added"
          : parentUpdated
          ? "updated"
          : "";
        paramState.parentAdded = false;
        paramState.parentUpdated = false;
        responseDialog(
          "success",
          "Success",
          `Parent ${actionType} successfully!`
        );
        if (!empty(location) && !empty(location.state)) {
          navigate(null, {
            replace: true,
            state: {
              parentAdded: false,
              parentUpdated: false,
            },
          });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // fetch parents
    try {
      getParents();
    } catch (error) {
      responseDialog(
        "error",
        "Something went wrong",
        "Failed to load parents. Please try again later."
      );
    } finally {
      navigate(null, {
        replace: true,
        state: { parentAdded: false, parentUpdated: false },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ user?._id, first, rows, searchValue]);

  // function to get parents
  const getParents = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const page = first / rows;
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await parentApi.getParents(schoolId, page, rows, searchValue);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setParentsData([]);
      }

      setTotalRecords(response_data?.count || 0);
      return setParentsData(
          isArray(response_data?.response)
          ? response_data.response
          : []
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  //function to toggle parent visibility
  const toggleParentVisiblity = async () => {
    try {
      if (parentsModalVisibility) setParentsModalVisibility(false);
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const active =
        !empty(selectedParentDetail) && !empty(selectedParentDetail.active)
          ? "Yes"
          : "No";
      const parentId =
        !empty(selectedParentDetail) && !empty(selectedParentDetail._id)
          ? selectedParentDetail._id
          : "";
      const response = await parentApi.toggleParentVisibility(
        parentId,
        active,
        schoolId
      );
      const response_data = prepareResponseData(response);
      const firstName = !empty(selectedParentDetail.firstName)
        ? selectedParentDetail.firstName
        : "";
      const lastName = !empty(selectedParentDetail.lastName)
        ? selectedParentDetail.lastName
        : "";
      const middleName = !empty(selectedParentDetail.middleName)
        ? selectedParentDetail.middleName
        : "";
      const title = !empty(selectedParentDetail.title)
        ? selectedParentDetail.title
        : "";
      const fullName = `${title} ${lastName} ${firstName} ${middleName}`;
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to ${
                !empty(selectedParentDetail) &&
                !empty(selectedParentDetail.active) &&
                selectedParentDetail.active === false
                  ? "enable"
                  : "disable"
              } parent with name, ${fullName}`;
        return responseDialog("error", "Something went wrong", error_response);
      }

      responseDialog(
        "success",
        "Success",
        `${fullName}, was ${
          !empty(selectedParentDetail) &&
          !empty(selectedParentDetail.active) &&
          selectedParentDetail.active === true
            ? "disabled"
            : "enabled"
        } successfully!`
      );
      return getParents();
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  //function to show confirm modal
  const showDisableConfirm = (data) => {
    setModalType("update");
    setSelectedParentsDetail(data);
    setParentsModalVisibility(true);
  };

  //function to hide confirm modal
  const hideParentModal = () => {
    setParentsModalVisibility(false);
  };

  //go to update parent page
  const gotToUpdateParent = (data) => {
    const parentId = !empty(data) && !empty(data._id) ? data._id : "";
    navigate(`/parents/${parentId}/update`);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      setSearchValue(event.target.value);
      setFirst(0);
    }
  };

  const onSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const parentModalFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: "#e65061",
          borderColor: "#e65061",
          borderWidth: 1,
        }}
        onClick={hideParentModal}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: "#389d17",
          borderColor: "#389d17",
          borderWidth: 1,
        }}
        onClick={() => toggleParentVisiblity()}
      />
    </div>
  );

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            {/*  header start */}
            <MainHeader
              button
              buttonText="Add Parent"
              icon={<FaPlus />}
              iconMarginRight={8}
              link="/parents/add"
              title="Parent"
            />
            {/* end of header */}

            {/* table start */}
            {!isLoading ? (
              <ParentsTable
                parents={parentsData}
                confirmDisableSelected={showDisableConfirm}
                goToUpdateParentScreen={gotToUpdateParent}
                onKeyDown={onKeyDown}
                onSearchChange={onSearchChange}
                totalRecords={totalRecords}
                first={first}
                search={search}
                rows={rows}
                onPageChange={onPageChange}
              />
            ) : (
              <TableLoading cols={4} rows={20} />
            )}
            {/* table end  */}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Dialog
        visible={parentsModalVisibility}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={parentModalFooter}
        onHide={hideParentModal}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: "#e65061" }}
          />
          {
            <span>
              Are you sure you want to{" "}
              {modalType === "update" ? (
                !empty(selectedParentDetail) &&
                !empty(selectedParentDetail.active) &&
                selectedParentDetail.active ? (
                  <span style={{ color: "red" }}>
                    <strong>disable</strong>
                  </span>
                ) : (
                  <span style={{ color: "green" }}>
                    <strong>enable</strong>
                  </span>
                )
              ) : (
                <span style={{ color: "red" }}>
                  <strong>delete</strong>
                </span>
              )}{" "}
              the selected parent -{" "}
              <strong>
                {!empty(selectedParentDetail) &&
                !empty(selectedParentDetail.title)
                  ? selectedParentDetail.title
                  : ""}
              </strong>
              {selectedParentDetail && (
                <b>
                  {!empty(selectedParentDetail.name)
                    ? selectedParentDetail.name
                    : ""}
                </b>
              )}
              ?
            </span>
          }
        </div>
      </Dialog>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default Parents;
